// case-studies/goldenopp.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/goldenopp.css";

import example1 from "../assets/goldenopp/goldenoppbanner.jpeg";
import example2 from "../assets/goldenopp/contextgolden.png";
import example3 from "../assets/goldenopp/azerbaijanflag.jpg";
import example4 from "../assets/goldenopp/thebuild.png";
import example5 from "../assets/goldenopp/graduation.png";

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

// images start slightly zoomed in and settle to normal scale
const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const GoldenOpp = () => {
  // scroll to top when this page mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto", // or "smooth" if you prefer
    });
  }, []);

  return (
    <main className="go-root">
      {/* Top title and meta */}
      <header className="go-header">
        <div className="go-header-inner">
          <div className="go-title-block">
            <motion.h1
              className="go-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              A GOLDEN OPPORTUNITY
            </motion.h1>
            <motion.p
              className="go-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              Turning chaos, impostor syndrome, and a rushed thesis into
              published research and a Golden Visa in the UAE.
            </motion.p>
          </div>

          <div className="go-meta-row">
            <motion.div
              className="go-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="go-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="go-meta-item">
                <span className="go-meta-label">Project Type</span>
                <span className="go-meta-value">Research case study</span>
              </div>
              <div className="go-meta-item">
                <span className="go-meta-label">Role</span>
                <span className="go-meta-value">
                  Lead researcher and developer
                </span>
              </div>
              <div className="go-meta-item">
                <span className="go-meta-label">Year</span>
                <span className="go-meta-value">2022</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Banner image */}
      <section className="go-banner">
        <div className="go-banner-inner">
          <motion.img
            src={example1}
            alt="Facial emotion recognition visual"
            className="go-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />

          {/* VIEW PAPER link */}
          <motion.a
            href="https://link.springer.com/chapter/10.1007/978-981-99-8324-7_15"
            className="go-view-paper-link"
            target="_blank"
            rel="noopener noreferrer"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            VIEW PAPER
          </motion.a>
        </div>
      </section>

      {/* 1. Hook - full width, centered title */}
      <section className="go-section go-section-full">
        <div className="go-section-inner">
          <motion.h2
            className="go-section-title go-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>
          <motion.div
            className="go-section-body go-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              It was 2022. COVID was still hanging around, my social life was
              fading, my relationship was falling apart, and online classes were{" "}
              <span className="go-emphasis-inline">
                draining whatever motivation I had left. Computer Science still
                felt like a world I had crashed into by accident.
              </span>{" "}
              My grades were not great, and my focus was scattered.
            </p>
            <p>
              Somewhere inside that chaos, I landed on a project that did not
              just fix my grades. It changed my life, and my family’s life, in a
              way I never saw coming.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. Context - title, image, text */}
      <section className="go-section go-section-split go-split-text-left">
        <div className="go-section-inner go-section-inner-split">
          <motion.h3
            className="go-section-title go-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h3>

          <div className="go-split-col go-split-col-media">
            <div className="go-image-frame">
              <motion.img
                src={example2}
                alt="Early university context"
                className="go-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="go-split-col go-split-col-text">
            <motion.div
              className="go-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                I{" "}
                did not come from a typical Computer Science background
                . I started in commerce and economics, and then jumped into a
                BSc in Computer Science surrounded by people who had been coding
                since they were kids. In my{" "}
                <span className="go-emphasis-inline">first year</span>, I
                somehow{" "}
                <span className="go-emphasis-inline">
                  picked up the Deputy Principal’s Award
                </span>{" "}
                for more than <span className="go-emphasis-inline">six A’s</span>
                , but most of that was me improvising my way through.
              </p>
              <p>
                <span className="go-emphasis-inline">
                  By my third year, I was sharper and better at problem solving.
                </span>{" "}
                But I still did not feel like the strongest person in the room.
                That was also the year I had to complete my final thesis. At the
                start, I honestly did not care enough about it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Challenge - title, image, text */}
      <section className="go-section go-section-split go-split-text-right">
        <div className="go-section-inner go-section-inner-split">
          <motion.h3
            className="go-section-title go-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h3>

          <div className="go-split-col go-split-col-media">
            <div className="go-image-frame">
              <motion.img
                src={example3}
                alt="Travel and timing challenge"
                className="go-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="go-split-col go-split-col-text">
            <motion.div
              className="go-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                In the first week of my final year, everyone was{" "}
                <span className="go-emphasis-inline">
                  supposed to find a thesis supervisor
                </span>
                . That same week,{" "}
                <span className="go-emphasis-inline">
                  I was on a trip to Azerbaijan
                </span>{" "}
                with my cousins. By the time I came back,{" "}
                <span className="go-emphasis-inline">
                  every professor was fully booked. Every single one.
                </span>
              </p>
              <p>
                The only person with a slot left was a newly joined assistant
                professor, Dr. Md Azher Uddin. He had just arrived from South
                Korea, carried a PhD from Kyung Hee University, and had a strong
                background in research and computer vision. He seemed{" "}
                <span className="go-emphasis-inline">
                  calm, sharp, and very intentional with his work
                </span>
                .
              </p>
              <p>
                I had nothing to lose, so I said{" "}
                <span className="go-emphasis-inline">
                  yes to working under him
                </span>
                . That simple decision turned out to be{" "}
                <span className="go-emphasis-inline">
                  the key moment of my twenties
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. Strategy - full width, centered title */}
      <section className="go-section go-section-full">
        <div className="go-section-inner">
          <motion.h2
            className="go-section-title go-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>
          <motion.div
            className="go-section-body go-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Up until that point, my academic approach was simple.{" "}
              <span className="go-emphasis-inline">
                Survive, pass, move on. Nothing more.
              </span>{" "}
              Dr. Uddin{" "}
              <span className="go-emphasis-inline">
                changed that by handing me a project that actually mattered
              </span>
              .
            </p>
            <p>
              He suggested that I{" "}
              <span className="go-emphasis-inline">
                reuse an algorithm he had designed
              </span>{" "}
              for depression recognition in elderly patients, and adapt it to a
              new domain.{" "}
              <span className="go-emphasis-inline">
                Facial expression recognition.
              </span>{" "}
              Both spontaneous macro expressions and subtle micro expressions. A
              field with{" "}
              <span className="go-emphasis-inline">
                real implications in areas like criminal justice, healthcare,
                and customer engagement
              </span>
              .
            </p>
            <p>
              For the first time, I felt like I was working on{" "}
              <span className="go-emphasis-inline">
                something that was bigger than just a grade
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. Build - title, image, text */}
      <section className="go-section go-section-split go-split-text-left">
        <div className="go-section-inner go-section-inner-split">
          <motion.h3
            className="go-section-title go-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Build
          </motion.h3>

          <div className="go-split-col go-split-col-media">
            <div className="go-image-frame">
              <motion.img
                src={example4}
                alt="System architecture and build"
                className="go-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="go-split-col go-split-col-text">
            <motion.div
              className="go-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                I designed an{" "}
                <span className="go-emphasis-inline">
                  end to end framework
                </span>{" "}
                that could{" "}
                <span className="go-emphasis-inline">process video</span> and{" "}
                <span className="go-emphasis-inline">
                  recognise both macro and micro facial expressions
                </span>
                . The system combined deep and handcrafted features instead of
                choosing one over the other.
              </p>
              <p>
                I used{" "}
                <span className="go-emphasis-inline">
                  ResNet50 to extract deep spatial features
                </span>{" "}
                from each frame,{" "}
                <span className="go-emphasis-inline">
                  applied Volume Local Directional Structural Pattern to capture
                  spatiotemporal information
                </span>
                , and then fed both into a{" "}
                <span className="go-emphasis-inline">
                  custom 1D Convolutional Neural Network
                </span>{" "}
                that{" "}
                <span className="go-emphasis-inline">classified</span> the
                emotion.
              </p>
              <p>
                For months I tuned parameters, cleaned up data, fought with
                preprocessing steps, and watched expression samples on repeat
                until they felt like real people and not just datasets. It was
                the first time in my degree where I was not pretending.{" "}
                <span className="go-emphasis-inline">
                  I was actually building.
                </span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. Outcome - title, image, text */}
      <section className="go-section go-section-split go-split-text-right">
        <div className="go-section-inner go-section-inner-split">
          <motion.h3
            className="go-section-title go-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Outcome
          </motion.h3>

          <div className="go-split-col go-split-col-media">
            <div className="go-image-frame">
              <motion.img
                src={example5}
                alt="Outcomes and Golden Visa"
                className="go-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="go-split-col go-split-col-text">
            <motion.div
              className="go-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                The work did more than just pass.{" "}
                <span className="go-emphasis-inline">It stood out.</span> I{" "}
                <span className="go-emphasis-inline">received an A</span> on my
                thesis, my final marks pushed me into{" "}
                <span className="go-emphasis-inline">
                  First Class Honors, and I secured a 4.0 GPA
                </span>
                . Considering how I started, that alone felt{" "}
                <span className="go-emphasis-inline">unreal</span>.
              </p>
              <p>
                A few months later, things escalated. Our class{" "}
                <span className="go-emphasis-inline">
                  group chat lit up with a message
                </span>{" "}
                that some students had been{" "}
                <span className="go-emphasis-inline">
                  nominated for the Dubai Golden Visa
                </span>{" "}
                under the{" "}
                <span className="go-emphasis-inline">
                  Outstanding Student
                </span>{" "}
                category. I had never seen myself as that kind of student, but{" "}
                <span className="go-emphasis-inline">I checked anyway</span>.
              </p>
              <p>
                <span className="go-emphasis-inline">I had been nominated</span>
                . I went to the immigration center{" "}
                <span className="go-emphasis-inline">
                  expecting some kind of error
                </span>
                . Instead, everything went through. In a single day, I{" "}
                <span className="go-emphasis-inline">
                  secured a ten year Golden Visa
                </span>{" "}
                and the ability to sponsor both my mother and father in the UAE.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. Reflection - title top left, full width text */}
      <section className="go-section go-section-full go-section-reflection">
        <div className="go-section-inner">
          <motion.h2
            className="go-section-title go-section-title-left"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>
          <motion.div
            className="go-section-body go-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              I was{" "}
              <span className="go-emphasis-inline">
                never the perfect student
              </span>
              . My start was messy. I improvised too much. I often{" "}
              <span className="go-emphasis-inline">
                felt like I did not belong in Computer Science at all
              </span>
              .
            </p>
            <p>
              This project taught me something important. A decision that looks
              careless in the moment can end up opening the biggest door in your
              life. My trip to Azerbaijan meant I missed the usual path and
              ended up taking a different one. That path gave me a supervisor
              who trusted me with serious work, a research project that meant
              something, a{" "}
              <span className="go-emphasis-inline">
                degree result that changed how people saw me
              </span>
              , and a{" "}
              <span className="go-emphasis-inline">
                visa that changed how my family lives
              </span>
              .
            </p>
            <p>
              This was my{" "}
              <span className="go-emphasis-gold-inline">
                golden opportunity
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* Optional closing visual */}
      {/* <section className="go-section go-closing-visual">
        <div className="go-section-inner">
          <div className="go-image-frame go-image-frame-soft">
            <motion.img
              src={example6}
              alt="Golden opportunity visual"
              className="go-image"
              variants={imageVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
          </div>
        </div>
      </section> */}
    </main>
  );
};

export default GoldenOpp;
